import { useEffect, useState } from 'react';
import { describeError } from '@/lib/api';
import {
  PASSKEY_LABELS,
  describePasskeyFailure,
  isPasskeySupported,
  listAccountPasskeys,
  listMyPasskeys,
  passkeyLabelText,
  registerPasskey,
  revokePasskey,
  type PasskeyDto,
  type PasskeyLabel,
  type PasskeyList,
} from '@/lib/passkeys';
import { useStepUpDialog } from '@/lib/step-up';
import { StepUpDialog } from './StepUpDialog';
import { SimpleConfirmDialog } from './SimpleConfirmDialog';

const dateTime = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
  timeZone: 'America/Sao_Paulo',
});

function formatDate(iso: string | null): string {
  return iso ? dateTime.format(new Date(iso)) : '—';
}

interface PasskeysCardProps {
  /**
   * `self`: as passkeys do operador logado, com cadastro e revogação. `account`: as de outra conta,
   * vistas por um Admin (`Operadores →` detalhe), só com revogação.
   */
  mode: 'self' | 'account';
  /** A conta, no modo `account`. */
  accountId?: string;
}

/**
 * Passkeys do operador (change platform-backoffice-hardening, C14, tarefa 14.6). A passkey só
 * confirma ações sensíveis (step-up), no lugar do código do autenticador; o login continua com
 * senha e código. O cadastro pede o código atual do autenticador (a passkey não cadastra outra) e
 * uma sessão aberta com senha e código. Até 5 ativas por conta.
 */
export function PasskeysCard({ mode, accountId }: PasskeysCardProps) {
  const [list, setList] = useState<PasskeyList | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [label, setLabel] = useState<PasskeyLabel>('notebook');
  const [revoking, setRevoking] = useState<PasskeyDto | null>(null);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const supported = isPasskeySupported();

  // Recarga pela troca do contador: o efeito busca a lista e só grava o estado na resposta.
  const [reloadTick, setReloadTick] = useState(0);
  const reload = () => setReloadTick((t) => t + 1);

  useEffect(() => {
    let cancelled = false;
    (mode === 'self' ? listMyPasskeys() : listAccountPasskeys(accountId ?? ''))
      .then((data) => {
        if (cancelled) return;
        setList(data);
        setLoadError(null);
      })
      .catch((err) => !cancelled && setLoadError(describeError(err)));
    return () => {
      cancelled = true;
    };
  }, [mode, accountId, reloadTick]);

  async function register(proof: string | { passkey: string }, confirmedLabel: PasskeyLabel) {
    // O cadastro aceita só o código: o modal não oferece passkey (sem `passkeyTarget`).
    if (typeof proof !== 'string') return;
    try {
      await registerPasskey(proof, confirmedLabel);
    } catch (err) {
      const browser = describePasskeyFailure(err);
      if (browser) throw new Error(browser);
      throw err;
    }
    setNotice('Passkey cadastrada. Ela já pode confirmar ações sensíveis no lugar do código.');
    reload();
  }

  const stepUp = useStepUpDialog<PasskeyLabel>(register, (err) => setError(describeError(err)));

  function startRegistration() {
    if (stepUp.open) return;
    setError(null);
    setNotice(null);
    stepUp.start(label);
  }

  async function confirmRevoke() {
    if (!revoking || revokeBusy) return;
    setRevokeBusy(true);
    setError(null);
    try {
      await revokePasskey(revoking.id);
      setNotice(`Passkey "${passkeyLabelText(revoking.label)}" revogada.`);
      setRevoking(null);
      reload();
    } catch (err) {
      setError(describeError(err));
      setRevoking(null);
    } finally {
      setRevokeBusy(false);
    }
  }

  const active = list?.items.filter((p) => p.active) ?? [];
  const revoked = list?.items.filter((p) => !p.active) ?? [];
  const atLimit = list ? list.activeCount >= list.maxActive : false;

  return (
    <div className="card">
      <h3 style={{ fontSize: 16 }}>Passkeys</h3>
      <p className="muted" style={{ fontSize: 14 }}>
        A passkey confirma ações sensíveis (desativar loja, cancelar assinatura, aplicar preço, reembolsar
        pacote, aprovar agente) no lugar do código do autenticador. Ela não substitui a senha nem o código no
        login. Administradores mantêm duas passkeys ativas (por exemplo, o notebook e uma chave física).
      </p>

      {loadError && <div className="error-banner">{loadError}</div>}
      {error && (
        <div className="alert alert-error" role="alert">
          {error}
        </div>
      )}
      {notice && <div className="info-banner">{notice}</div>}

      {list && (
        <>
          <p style={{ fontSize: 14, margin: '8px 0' }}>
            {list.activeCount} de {list.maxActive} ativas.
          </p>
          {active.length === 0 ? (
            <p className="muted" style={{ fontSize: 14 }}>
              Nenhuma passkey ativa.
            </p>
          ) : (
            <table className="table" aria-label="Passkeys ativas">
              <thead>
                <tr>
                  <th>Rótulo</th>
                  <th>Cadastrada em</th>
                  <th>Último uso</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {active.map((p) => (
                  <tr key={p.id}>
                    <td>{passkeyLabelText(p.label)}</td>
                    <td>{formatDate(p.createdAt)}</td>
                    <td>{formatDate(p.lastUsedAt)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn-secondary btn"
                        onClick={() => {
                          setError(null);
                          setNotice(null);
                          setRevoking(p);
                        }}
                      >
                        Revogar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {revoked.length > 0 && (
            <p className="muted" style={{ fontSize: 13 }}>
              {revoked.length} revogada(s).
            </p>
          )}
        </>
      )}

      {mode === 'self' &&
        (supported ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 12 }}>
            <div className="field" style={{ margin: 0 }}>
              <label htmlFor="passkey-label">Rótulo</label>
              <select
                id="passkey-label"
                value={label}
                onChange={(e) => setLabel(e.target.value as PasskeyLabel)}
                disabled={atLimit || stepUp.open}
              >
                {PASSKEY_LABELS.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.text}
                  </option>
                ))}
              </select>
            </div>
            <button type="button" className="btn" onClick={startRegistration} disabled={atLimit || stepUp.open || !list}>
              Cadastrar passkey
            </button>
          </div>
        ) : (
          <p className="muted" style={{ fontSize: 14 }}>
            Este navegador não oferece passkey. Use um navegador atual para cadastrar.
          </p>
        ))}
      {mode === 'self' && atLimit && (
        <p className="muted" style={{ fontSize: 13 }}>
          Limite de passkeys ativas atingido: revogue uma antes de cadastrar outra.
        </p>
      )}

      {stepUp.open && (
        <StepUpDialog
          title="Cadastrar passkey"
          description={
            <p style={{ margin: 0 }}>
              Confirme com o código atual do autenticador. Depois, o navegador pede para criar a passkey (
              {passkeyLabelText(label)}).
            </p>
          }
          confirmLabel="Continuar"
          busy={stepUp.busy}
          error={stepUp.error}
          onSubmit={stepUp.submit}
          onCancel={stepUp.cancel}
        />
      )}

      {revoking && (
        <SimpleConfirmDialog
          title="Revogar passkey"
          body={`A passkey "${passkeyLabelText(revoking.label)}" deixa de confirmar ações sensíveis. Esta ação não se desfaz.`}
          confirmLabel="Revogar"
          destructive
          submitting={revokeBusy}
          onConfirm={() => void confirmRevoke()}
          onCancel={() => setRevoking(null)}
        />
      )}
    </div>
  );
}
